import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LoginNavbar } from '../../components/layout/Navbar';
import { useTranslation } from 'react-i18next';
import timesheetLogo from '../../assets/images/timesheet-logo.png';
import './ResetPassword.css';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const ResetPassword = () => {

  const { t, i18n } = useTranslation();
  useEffect(() => {
      document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Lock scroll and page height
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenChecking, setTokenChecking] = useState(true);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    // Validate the reset token when component mounts
    validateToken();
  }, [token]);

  const validateToken = async () => {
    setTokenChecking(true);
    
    if (!token) {
      setTokenValid(false);
      setTokenChecking(false);
      return;
    }

    try {
      // Simulate API call to validate token
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, consider token valid if it exists
      // In real implementation, you would validate with your backend
      setTokenValid(true);
    } catch (err) {
      setTokenValid(false);
    } finally {
      setTokenChecking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = t("resetPassword.passwordIsRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("resetPassword.characterRequirementError");
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = t("resetPassword.caseRequirementError");
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("resetPassword.confirmPassword");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("resetPassword.confirmPasswordError");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}user/reset-password`,
        null,
        {
          params: {
            token: token ,
            newPassword: formData.password
          }
        }
      );
      setIsSubmitted(true);
    } catch (err) {
      setErrors({ submit: t("resetPassword.resetError") });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // ...existing code...
  // Unified card layout for all states
  return (
    <>
      <LoginNavbar />
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <img src={timesheetLogo} alt="Timesheet Logo" className="reset-password-logo" />
            <h1 className="reset-password-title">
              {tokenChecking
                ? t("resetPassword.verifyingResetLink")
                : !tokenValid
                  ? t("resetPassword.invalidResetLink")
                  : isSubmitted
                    ? t("resetPassword.passwordResetSuccessful")
                    : t("resetPassword.resetYourPassword")}
            </h1>
          </div>
          {/* Subtitle for email */}
          {!tokenChecking && tokenValid && !isSubmitted && email && (
            <p className="reset-password-subtitle">
              {t("resetPassword.subtitle1")} <strong>{email}</strong> {t("resetPassword.subtitle2")}
            </p>
          )}
          {/* Loading spinner */}
          {tokenChecking && (
            <div className="loading-container" style={{ margin: '32px 0' }}>
              <div className="loading-spinner large"></div>
              <p>{t("resetPassword.loadingPageText")}</p>
            </div>
          )}
          {/* Invalid token */}
          {!tokenChecking && !tokenValid && (
            <div className="error-content">
              <div className="error-icon">⚠️</div>
              <p className="error-text">
                {t("resetPassword.errorText")}
              </p>
              <p className="error-subtext">
                {t("resetPassword.errorSubtext")}
              </p>
              <div className="error-actions">
                <Link to="/forgot-password" className="request-new-button">
                  {t("resetPassword.requestNewResetLink")}
                </Link>
                <Link to="/login" className="back-to-login">
                  {t("resetPassword.backToLogin")}
                </Link>
              </div>
            </div>
          )}
          {/* Success state */}
          {!tokenChecking && tokenValid && isSubmitted && (
            <div className="success-content">
              <div className="success-icon">✓</div>
              <p className="success-text">
                {t("resetPassword.successText")}
              </p>
              <p className="success-subtext">
                {t("resetPassword.successSubtext")}
              </p>
              <button 
                onClick={handleBackToLogin}
                className="add-document-back-btn"
              >
                {t("resetPassword.continueToLogin")}
              </button>
            </div>
          )}
          {/* Main form */}
          {!tokenChecking && tokenValid && !isSubmitted && (
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  {t("resetPassword.newPassword")}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder={t("resetPassword.promptPassword")}
                  disabled={isLoading}
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  {t("resetPassword.confirmNewPassword")}
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder={t("resetPassword.promptConfirmPassword")}
                  disabled={isLoading}
                />
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>
              <div className="password-requirements">
                <h4>{t("resetPassword.passwordRequirements")}</h4>
                <ul>
                  <li className={formData.password.length >= 8 ? 'valid' : ''}>
                    {t("resetPassword.lengthRequirement")}
                  </li>
                  <li className={/(?=.*[a-z])/.test(formData.password) ? 'valid' : ''}>
                    {t("resetPassword.lowerCaseRequirement")}
                  </li>
                  <li className={/(?=.*[A-Z])/.test(formData.password) ? 'valid' : ''}>
                    {t("resetPassword.upperCaseRequirement")}
                  </li>
                  <li className={/(?=.*\d)/.test(formData.password) ? 'valid' : ''}>
                    {t("resetPassword.numberRequirement")}
                  </li>
                </ul>
              </div>
              {errors.submit && (
                <div className="submit-error-message">{errors.submit}</div>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? t("resetPassword.loadingText") : t("resetPassword.resetPassword")}
                </button>
                <button 
                  type="button" 
                  className="submit-button" 
                  onClick={handleBackToLogin}
                  style={{ flex: 1, background: '#f5f5f5', color: '#2c3e50', border: '1px solid #e1e8ed' }}
                >
                  {t("resetPassword.cancel")}
                </button>
              </div>
            </form>
          )}
          {/* Footer */}
          {!tokenChecking && tokenValid && !isSubmitted && (
            <div className="reset-password-footer">
              {t("resetPassword.rememberYourPassword")} <Link to="/login" className="login-link">{t("resetPassword.signIn")}</Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
